// Friends + friend-requests hub (Phase 10 / spec #20 / #21).
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { friendsApi } from '@/api/friendsApi';
import { ApiException } from '@/api/errors';
import { formatRelative } from '@/utils/format';
import type { FriendEntry, FriendRequestEntry, UserSearchHit } from '@/api/types';

export function FriendsScreen() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [friends, setFriends] = useState<FriendEntry[]>([]);
  const [incoming, setIncoming] = useState<FriendRequestEntry[]>([]);
  const [outgoing, setOutgoing] = useState<FriendRequestEntry[]>([]);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchHits, setSearchHits] = useState<UserSearchHit[]>([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  async function refresh() {
    setBusy(true);
    setError(null);
    try {
      const [friendList, requestList] = await Promise.all([
        friendsApi.list(getToken),
        friendsApi.listRequests(getToken),
      ]);
      setFriends(friendList.rows);
      setIncoming(requestList.incoming);
      setOutgoing(requestList.outgoing);
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setError(err?.message ?? 'Could not load friends.');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search over /friends/search.
  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (trimmed.length < 2) {
      setSearchHits([]);
      setSearchError(null);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      setSearchBusy(true);
      setSearchError(null);
      try {
        const hits = await friendsApi.search(getToken, trimmed, 20);
        if (!cancelled) setSearchHits(hits);
      } catch (e) {
        if (cancelled) return;
        const err = e instanceof ApiException ? e : null;
        setSearchError(err?.message ?? 'Search failed.');
      } finally {
        if (!cancelled) setSearchBusy(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [searchTerm, getToken]);

  const filteredFriends = useMemo(() => {
    if (search.trim() === '') return friends;
    const q = search.toLowerCase();
    return friends.filter(
      (f) => f.username.toLowerCase().includes(q) || (f.email?.toLowerCase().includes(q) ?? false),
    );
  }, [friends, search]);

  async function handleSendRequest(receiverId: string) {
    try {
      await friendsApi.sendRequest(getToken, { receiver_id: receiverId });
      await refresh();
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setError(err?.message ?? 'Could not send friend request.');
    }
  }

  async function handleAccept(id: string) {
    try {
      await friendsApi.acceptRequest(getToken, id);
      await refresh();
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setError(err?.message ?? 'Could not accept request.');
    }
  }

  async function handleReject(id: string) {
    try {
      await friendsApi.rejectRequest(getToken, id);
      await refresh();
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setError(err?.message ?? 'Could not decline request.');
    }
  }

  async function handleCancel(id: string) {
    try {
      await friendsApi.cancelRequest(getToken, id);
      await refresh();
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setError(err?.message ?? 'Could not cancel request.');
    }
  }

  async function handleRemove(friendUserId: string) {
    if (!window.confirm('Remove this friend?')) return;
    try {
      await friendsApi.removeFriend(getToken, friendUserId);
      await refresh();
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setError(err?.message ?? 'Could not remove friend.');
    }
  }

  return (
    <div className="stack">
      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0 }}>Friends</h1>
            <p className="muted" style={{ margin: '4px 0 0' }}>
              Connect with other reporters to share work selectively.
            </p>
          </div>
          <button type="button" className="btn btn--primary" onClick={refresh}>
            Refresh
          </button>
        </div>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      {incoming.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Friend requests</h2>
          <div className="list">
            {incoming.map((req) => (
              <div key={req.id} className="list__item" style={{ cursor: 'default' }}>
                <div style={{ flex: 1 }}>
                  <div className="list__item-title">
                    {req.sender_username ?? 'Someone'} wants to be your friend.
                  </div>
                  <div className="list__item-subtitle">{formatRelative(req.created_at)}</div>
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => handleAccept(req.id)}
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => handleReject(req.id)}
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {outgoing.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Sent requests</h2>
          <div className="list">
            {outgoing.map((req) => (
              <div key={req.id} className="list__item" style={{ cursor: 'default' }}>
                <div style={{ flex: 1 }}>
                  <div className="list__item-title">
                    Waiting for {req.receiver_username ?? 'someone'}
                  </div>
                  <div className="list__item-subtitle">{formatRelative(req.created_at)}</div>
                </div>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => handleCancel(req.id)}
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Find a friend</h2>
        <div className="field">
          <input
            type="search"
            className="input"
            placeholder="Type a username or email to search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="hint">
            Searches run against the user directory and respect privacy settings.
          </span>
        </div>
        {searchError ? <div className="error-banner">{searchError}</div> : null}
        {searchBusy ? <div className="empty">Searching…</div> : null}
        {!searchBusy && searchTerm.trim().length >= 2 && searchHits.length === 0 ? (
          <div className="empty">No users match this search.</div>
        ) : null}
        {searchHits.length > 0 ? (
          <div className="list">
            {searchHits.map((hit) => (
              <SearchResultRow
                key={hit.id}
                hit={hit}
                onSendRequest={() => handleSendRequest(hit.id)}
                onViewProfile={() => navigate(`/profile?user=${encodeURIComponent(hit.id)}`)}
              />
            ))}
          </div>
        ) : null}
      </div>

      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>My friends</h2>
          <input
            type="search"
            className="input"
            style={{ maxWidth: 260 }}
            placeholder="Search friends"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {busy ? (
          <div className="empty">Loading…</div>
        ) : filteredFriends.length === 0 ? (
          <div className="empty">
            {search.trim() === ''
              ? 'You have not added any friends yet.'
              : 'No friends match this search.'}
          </div>
        ) : (
          <div className="list">
            {filteredFriends.map((friend) => (
              <div key={friend.friend_user_id} className="list__item" style={{ cursor: 'default' }}>
                <div style={{ flex: 1 }}>
                  <div className="list__item-title">{friend.username}</div>
                  <div className="list__item-subtitle">
                    {friend.email
                      ? friend.email
                      : `Friends since ${formatRelative(friend.created_at)}`}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => handleRemove(friend.friend_user_id)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="muted" style={{ fontSize: 13 }}>
        Need to share a report? <Link to="/shares">Manage shared reports &rarr;</Link>
      </div>
    </div>
  );
}

function SearchResultRow({
  hit,
  onSendRequest,
  onViewProfile,
}: {
  hit: UserSearchHit;
  onSendRequest: () => void;
  onViewProfile: () => void;
}) {
  const isSelf = hit.is_self === 1;
  const alreadyFriend = hit.already_friend === 1;
  const blocked = hit.blocked_by_me === 1;
  const outgoing = hit.request_outgoing === 1;
  const incoming = hit.request_incoming === 1;
  const canSend = !isSelf && !alreadyFriend && !blocked && !outgoing;
  const counterTitle =
    hit.username +
    (hit.email ? ` <${hit.email}>` : '') +
    (isSelf ? ' (you)' : alreadyFriend ? ' (already a friend)' : blocked ? ' (blocked)' : '');
  return (
    <div className="list__item" style={{ cursor: 'default' }}>
      <div style={{ flex: 1 }}>
        <div className="list__item-title" title={counterTitle}>
          {hit.username}
        </div>
        <div className="list__item-subtitle">{hit.email ?? 'No email on file.'}</div>
      </div>
      <div className="row" style={{ gap: 8 }}>
        {isSelf ? (
          <span className="pill">You</span>
        ) : alreadyFriend ? (
          <span className="pill pill--submitted">Friend</span>
        ) : blocked ? (
          <span className="pill pill--deleted">Blocked</span>
        ) : outgoing ? (
          <span className="pill pill--draft">Requested</span>
        ) : incoming ? (
          <span className="pill pill--submitted">Incoming</span>
        ) : (
          <button
            type="button"
            className="btn btn--primary"
            disabled={!canSend}
            onClick={onSendRequest}
          >
            Add friend
          </button>
        )}
        <button type="button" className="btn btn--ghost" onClick={onViewProfile}>
          View
        </button>
      </div>
    </div>
  );
}
